import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import WSDataGrid, { IColumns } from "../../Components/DataGrid/WSDataGrid";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { callAPI, Method, updateMainHeader } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import WSFileUpload from "../../Components/FileUpload/WSFileUpload";
import http from "../../commonFunctions/httpClient";

const extractBackendErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  let message = fallback;

  if (typeof data === "string") {
    message = data;
  } else if (typeof data?.detail === "string") {
    message = data.detail;
  } else if (Array.isArray(data?.detail)) {
    message = data.detail[0]?.msg || fallback;
  } else if (data?.message) {
    message = data.message;
  } else if (error?.message) {
    message = error.message;
  }

  return message.length > 100 ? `${message.slice(0, 100)}...` : message;
};

export const PurchaseList: React.FC<any> = React.memo(() => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();

  const [selectedId, setSelectedId] = useState<string | number>(0);
  const [isBillPopupOpen, setBillPopupOpen] = useState(false);
  const [billLoading, setBillLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const {
    control: billControl,
    handleSubmit: handleBillSubmit,
    reset: resetBillForm,
  } = useForm<any>({
    defaultValues: { file: [] },
  });

  useEffect(() => {
    updateMainHeader("Purchase");
    breadCrumbsStore.updateBreadCrumbs([{ Bills: "/purchase/list" }]);
  }, []);

  const source = {
    url: "purchaseorder/list",
    page: 1,
    pageSize: 10,
    filter: [],
    sort: [],
    type: "GET" as const,
  };

  const handleUploadBill = async (data: any) => {
    if (!selectedId) return;
    if (!data.file || data.file.length === 0) {
      showMessage("Please select a bill file to upload", "error");
      return;
    }

    try {
      setBillLoading(true);
      const formData = new FormData();
      formData.append("id", String(selectedId));
      formData.append("file", data.file[0]);

      const response = await callAPI(
        `purchaseorder/${selectedId}/bill`,
        Method.POSTFORMDATA,
        undefined,
        undefined,
        formData
      );

      if (response?.success !== false) {
        showMessage("Bill uploaded successfully!", "success");
        setBillPopupOpen(false);
        resetBillForm({ file: [] });
        setRefreshKey((prev) => prev + 1);
      } else {
        showMessage(response?.message || "Failed to upload bill", "error");
      }
    } catch (error: any) {
      console.error("Upload Bill Error:", error?.response?.data || error);
      showMessage(extractBackendErrorMessage(error, "Failed to upload bill"), "error");
    } finally {
      setBillLoading(false);
    }
  };

  const handleViewBill = async (purchaseOrderId: string | number) => {
    try {
      const result: any = await http.get(`purchaseorder/${purchaseOrderId}/bill`, {
        responseType: "blob",
      });

      if (!(result instanceof Blob)) {
        showMessage("Failed to load bill", "error");
        return;
      }

      if (result.type && result.type.includes("json")) {
        let message = "No bill uploaded for this purchase order";
        try {
          const text = await result.text();
          const parsed = JSON.parse(text);
          message = parsed?.message || parsed?.detail || message;
        } catch {
          // keep default message
        }
        showMessage(message, "error");
        return;
      }

      const blobUrl = URL.createObjectURL(result);
      window.open(blobUrl, "_blank");
    } catch (error: any) {
      console.error("View Bill Error:", error);
      showMessage("Failed to load bill", "error");
    }
  };

  const columns: IColumns[] = [
    { header: "Date", fieldName: "CreatedDate", type: "date" },
    { header: "PO Number", fieldName: "PONumber" },
    { header: "Supplier", fieldName: "SupplierName" },
    { header: "Total Amount", fieldName: "TotalAmount", type: "amount" },
    { header: "Bill File", fieldName: "FileName" },
    { header: "Status", fieldName: "Status", type: "status" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["View Bill", "Upload Bill"],
      actionCallback: (action: string, row: any) => {
        const purchaseOrderId = row.PurchaseOrderId || row.id;

        if (!purchaseOrderId) {
          console.error("No valid ID found in row:", row);
          showMessage("Invalid purchase order data - missing ID", "error");
          return;
        }

        if (action === "View Bill") {
          handleViewBill(purchaseOrderId);
        } else if (action === "Upload Bill") {
          setSelectedId(purchaseOrderId);
          resetBillForm({ file: [] });
          setBillPopupOpen(true);
        }
      },
    },
  ];

  return (
    <Grid p={3}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <WSDataGrid
            id="purchaseBillGrid"
            key={refreshKey}
            title="Bills"
            headerType="modern"
            columns={columns}
            source={source}
            rowsPerPageCount={10}
            searchBox={{ searchMode: "query" }}
            gridHeaderSx={{
              backgroundColor: "whitesmoke",
              border: "1px solid whitesmoke",
            }}
          />
        </Grid>

        <Dialog open={isBillPopupOpen} onClose={() => setBillPopupOpen(false)}>
          <DialogTitle>Upload Purchase Order Bill</DialogTitle>
          <DialogContent sx={{ minWidth: 360 }}>
            <WSFileUpload
              id="bill-file"
              name="file"
              label="Bill File"
              control={billControl}
              acceptFileTypes=".pdf,.jpg,.jpeg,.png"
              maxFileSizeMB={5}
              multiple={false}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBillPopupOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBillSubmit(handleUploadBill)}
              disabled={billLoading}
            >
              Upload
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
});

export default PurchaseList;
